<template>
  <v-tooltip
    :disabled="tooltipDisabled"
    bottom>
    <template v-slot:activator="{ on, attrs }">
      <div
        v-on="on"
        v-bind="attrs"
        @keydown.capture="emitKeydown"
        @click.capture="onClickCapture">
        <v-text-field
          ref="textInput"
          v-on="mergeListeners(inputListeners)"
          v-bind="mergeAttrs($attrs, attrs)"
          :value="value"
          :class="inputClass"
          outlined
          :color="defaultColor"
          :persistent-placeholder="persistentPlaceholder"
          @input="$emit('input', $event)"
          :style="inputStyle">
          <template v-slot:append>
            <slot name="append" />
          </template>
        </v-text-field>
      </div>
    </template>
    <span>
      {{ tooltipText }}
    </span>
  </v-tooltip>
</template>

<script>
import Formatters from '@/shared/formatters/formatters';
export default {
  name: "TextField",

  inheritAttrs: false,

  props: {
    value: [String, Number, Boolean, Object, Array],

    regular: {
      type: Boolean,
      default: false,
    },

    outlined: {
      type: Boolean,
      default: false,
    },

    defaultColor: {
      type: String,
      default: 'primary',
    },

    textColor: {
      type: String,
      default: '',
    },

    persistentPlaceholder: {
      type: Boolean,
      default: true,
    },

    borderSelected: {
      type: Boolean,
      default: false,
    },

    showTooltip: {
      type: Boolean,
      default: false,
    },

    tooltipValue: {
      default: false,
    },
  },

  computed: {
    inputListeners() {
      const listeners = Object.assign({}, this.$listeners);
      delete listeners.keydown;
      return listeners;
    },

    mainClass() {
      return {
        'textfield-default': true,
        'textfield-border-selected': this.hasValue && this.borderSelected,
        'textfield-persist-placeholder': this.persistentPlaceholder,
        'textfield-regular': this.regular,
        'textfield-disabled': this.isDisabled,
        'textfield-text-color': this.textColor !== '',
      };
    },

    inputClass() {
      const attrs = this.$attrs;
      const data = this.$vnode && this.$vnode.data;
      const classes = [];
      if (data) {
        if (data.staticClass) classes.push(data.staticClass);
        if (data.class) classes.push(data.class);
      }
      return [
        this.mainClass,
        attrs.class,
        ...classes,
      ];
    },

    inputStyle() {
      const attrs = this.$attrs;
      const data = this.$vnode && this.$vnode.data;
      const styles = [];
      if (data) {
        if (data.staticStyle) styles.push(data.staticStyle);
        if (data.style) styles.push(data.style);
      }
      if (this.textColor !== '') {
        styles.push({ '--textfield-text-color': `var(--v-${this.textColor}-base)` });
      }
      return [
        attrs.style,
        ...styles,
      ];
    },

    isDisabled() {
      return this.$attrs.disabled !== undefined && this.$attrs.disabled !== false;
    },

    hasValue() {
      const formatter = new Formatters();
      if (formatter.isEmptyValue(this.value)) return false;
      return true;
    },

    tooltipSourceValue() {
      return this.tooltipValue !== false ? this.tooltipValue : this.value;
    },

    tooltipText() {
      return this.formatTooltipValue(this.tooltipSourceValue);
    },

    tooltipDisabled() {
      return !this.showTooltip || this.isTooltipEmpty(this.tooltipSourceValue);
    },
  },

  methods: {
    focus() {
      const input = this.$refs.textInput;
      if (input && typeof input.focus === 'function') {
        input.focus();
      }
    },

    emitKeydown(event) {
      this.$emit('keydown', event);
    },

    onClickCapture(event) {
      this.$emit('click', event);
    },

    mergeListeners(listeners, tooltipOn) {
      const merged = Object.assign({}, listeners);
      if (!tooltipOn) return merged;
      Object.keys(tooltipOn).forEach((event) => {
        const current = merged[event];
        if (current) {
          merged[event] = Array.isArray(current) ? current.concat(tooltipOn[event]) : [current, tooltipOn[event]];
          return;
        }
        merged[event] = tooltipOn[event];
      });
      return merged;
    },

    mergeAttrs(attrs, tooltipAttrs) {
      return Object.assign({}, attrs, tooltipAttrs);
    },

    formatTooltipValue(value) {
      if (Array.isArray(value)) {
        return value
          .map((item) => this.formatSingleTooltipValue(item))
          .filter((item) => item !== '')
          .join(', ');
      }
      return this.formatSingleTooltipValue(value);
    },

    formatSingleTooltipValue(value) {
      if (this.isTooltipEmpty(value)) return '';
      if (value && typeof value === 'object') {
        return this.formatPrimitiveTooltipValue(this.getItemText(value));
      }
      const itemFromList = this.findItemFromItems(value);
      if (itemFromList) {
        return this.formatPrimitiveTooltipValue(this.getItemText(itemFromList));
      }
      return this.formatPrimitiveTooltipValue(value);
    },

    formatPrimitiveTooltipValue(value) {
      if (this.isTooltipEmpty(value)) return '';
      if (typeof value === 'string') return value;
      if (typeof value === 'number' || typeof value === 'boolean') return String(value);
      if (value && typeof value === 'object') {
        try {
          return JSON.stringify(value);
        } catch (error) {
          return String(value);
        }
      }
      return String(value);
    },

    getItemText(item) {
      const itemText = this.$attrs['item-text'] || this.$attrs.itemText;
      if (typeof itemText === 'function') {
        return itemText(item);
      }
      if (itemText && item && typeof item === 'object' && itemText in item) {
        return item[itemText];
      }
      if (item && typeof item === 'object') {
        if ('text' in item) return item.text;
        if ('name' in item) return item.name;
        if ('label' in item) return item.label;
        if ('description' in item) return item.description;
      }
      return item;
    },

    getItemValue(item) {
      const itemValue = this.$attrs['item-value'] || this.$attrs.itemValue;
      if (typeof itemValue === 'function') {
        return itemValue(item);
      }
      if (itemValue && item && typeof item === 'object' && itemValue in item) {
        return item[itemValue];
      }
      if (item && typeof item === 'object') {
        if ('value' in item) return item.value;
        if ('id' in item) return item.id;
      }
      return item;
    },

    findItemFromItems(value) {
      const items = this.$attrs.items;
      if (!Array.isArray(items)) return null;
      return items.find((item) => {
        if (item && typeof item === 'object') {
          return this.getItemValue(item) === value;
        }
        return item === value;
      });
    },

    isTooltipEmpty(value) {
      const formatter = new Formatters();
      if (Array.isArray(value)) return value.length === 0;
      if (value && typeof value === 'object') return Object.keys(value).length === 0;
      return formatter.isEmptyValue(value);
    },
  },
}
</script>

<style lang="scss">
.textfield-default .v-input__slot {
  display: flex !important;
  align-items: center !important;
}

.textfield-default label {
  top: unset !important;
}

.textfield-default .v-text-field__details {
  margin-bottom: 5px !important;
}

.textfield-default.v-input {
  align-items: center !important;
}

.textfield-default :is([class*="v-input__prepend"], [class*="v-input__append"]) {
  height: 50px !important;
  display: flex !important;
  align-items: center !important;
}

.textfield-default.v-text-field--full-width .v-input__prepend-outer,
.textfield-default.v-text-field--full-width .v-input__prepend-inner,
.textfield-default.v-text-field--full-width .v-input__append-inner,
.textfield-default.v-text-field--full-width .v-input__append-outer,
.textfield-default.v-text-field--enclosed .v-input__prepend-outer,
.textfield-default.v-text-field--enclosed .v-input__prepend-inner,
.textfield-default.v-text-field--enclosed .v-input__append-inner,
.textfield-default.v-text-field--enclosed .v-input__append-outer {
  margin-top: 0 !important;
}

.textfield-border-selected:not(.v-input--is-focused):not(.v-input--has-state)
  > .v-input__control
    > .v-input__slot
      fieldset {
  color: var(--v-primary-base) !important;
}

.textfield-default:not(:is([class*="field--disabled"])):is([class*="v-input--is-disabled"])
  > .v-input__control
    > .v-input__slot
      fieldset {
  color: color-mix(in srgb, var(--v-border-base) 40%, transparent) !important;
}

.textfield-default:not(:is([class*="field--disabled"])):is([class*="v-input--is-disabled"])
  > .v-input__control
    > .v-input__slot {
      label,
      input,
      i {
        color: color-mix(in srgb, var(--v-foreground-base) 40%, transparent) !important;
      }
}

.textfield-default:not(.textfield-border-selected):not(.v-input--is-focused):not(.v-input--has-state):not(:is([class*="field--disabled"])):not(:is([class*="v-input--is-disabled"]))
  > .v-input__control
    > .v-input__slot
      fieldset {
  color: var(--v-border-base) !important;
}

.textfield-default fieldset {
  border-width: 2px;
}

.textfield-default {
  > .v-input__control {
    ::before {
      border-image: none !important;
    }

    > .v-input__slot {
      padding: 0 12px !important;
      min-height: 50px;
      margin: 0;

      > .v-text-field__slot {
        margin: 0 !important;
        height: 100%;
      }

      &::before,
      &::after,
      :before,
      :after {
        border-color: transparent !important;
        background-color: transparent !important;
      }

      label {
        padding: 0 8px;
        font-size: 16px;
        color: var(--v-foreground-base);
        max-width: 133%;
      }
    }
  }
}

.textfield-regular {
  margin: 0px !important;
  padding: 0px !important;

  > .v-input__control {
    > .v-input__slot {
      margin-bottom: 1px;
    }
  }
}

.textfield-persist-placeholder {
  > .v-input__control {
    > .v-input__slot {
      label {
        background-color: transparent;
        transform: translate(-6px, -26px) scale(0.75);
        pointer-events: auto;
      }
    }
  }
}

.textfield-default > .v-input__control > .v-input__slot {
  > .v-text-field__slot > input {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: var(--v-foreground-base);
  }
}

.textfield-disabled > .v-input__control > .v-input__slot {
  background: var(--v-muted-base);
}

.textfield-text-color input {
  color: var(--textfield-text-color) !important;
}
</style>
